import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { FiHome, FiCheckCircle, FiBarChart2, FiAlertTriangle, FiPlayCircle } from "react-icons/fi";
import { getResults, getSessionCategoryBreakdown } from "../api/client";

export default function Results() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [results, setResults] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    Promise.all([
      getResults(sessionId).then((r) => r.data).catch(() => null),
      getSessionCategoryBreakdown(sessionId).then((r) => r.data).catch(() => null),
    ]).then(([resultsData, catData]) => {
      setResults(resultsData);
      setCategories(catData?.categories || []);
      setLoading(false);
    });
  }, [sessionId]);

  if (loading) {
    return (
      <div className="results-page">
        <div className="loading-screen">
          <div className="spinner" />
        </div>
      </div>
    );
  }

  const scorePct = results?.score_percentage;
  const hasScore = typeof scorePct === "number";

  // Recovery path: if the session landed on results with fewer answers than
  // total_questions, it was likely force-submitted by a glitch. Offer a Resume
  // button — the backend's /resume un-completes glitched sessions automatically.
  const totalQ = results?.session?.total_questions ?? 0;
  const answeredQ = results?.responses?.length ?? 0;
  const hasUnanswered = totalQ > 0 && answeredQ < totalQ;

  const handleResume = () => {
    try {
      sessionStorage.setItem("aiq_session_id", String(sessionId));
      sessionStorage.setItem("aiq_current_index", "0");
    } catch {}
    navigate("/quiz");
  };

  return (
    <div className="results-page">
      <div className="results-container">
        {hasUnanswered && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              marginBottom: "24px",
              padding: "20px 24px",
              borderRadius: "14px",
              background: "linear-gradient(135deg, rgba(239,68,68,0.15), rgba(220,38,38,0.12))",
              border: "1px solid rgba(239, 68, 68, 0.35)",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            <FiAlertTriangle size={28} color="#ef4444" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: "240px" }}>
              <div style={{ fontWeight: 700, fontSize: "15px", color: "#f1f5f9", marginBottom: "4px" }}>
                Your attempt looks incomplete
              </div>
              <div style={{ fontSize: "13px", color: "#cbd5e1" }}>
                You answered {answeredQ} of {totalQ} questions. If this was caused by a glitch, you can resume and finish the remaining {totalQ - answeredQ} question{totalQ - answeredQ === 1 ? "" : "s"}.
              </div>
            </div>
            <button
              onClick={handleResume}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "10px 22px",
                background: "linear-gradient(135deg, #ef4444, #dc2626)",
                color: "#fff",
                border: "none",
                borderRadius: "10px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              <FiPlayCircle size={18} />
              Resume Assessment
            </button>
          </motion.div>
        )}
        <motion.div
          className="score-hero"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <motion.div
            className="thank-you-icon"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 200 }}
          >
            <FiCheckCircle />
          </motion.div>

          <motion.h1
            className="thank-you-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            Thank You!
          </motion.h1>

          <motion.p
            className="thank-you-message"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            Your AIQ Assessment has been submitted successfully.
          </motion.p>

          {hasScore && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.5 }}
              style={{
                marginTop: "18px",
                padding: "24px 32px",
                borderRadius: "16px",
                background: "rgba(255,255,255,0.06)",
                border: "2px solid var(--border-subtle)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                minWidth: "240px",
              }}
            >
              <span style={{
                fontSize: "12px",
                textTransform: "uppercase",
                letterSpacing: "1px",
                fontWeight: 600,
                color: "var(--text-secondary)",
              }}>
                Your Score
              </span>
              <span style={{
                fontSize: "56px",
                fontWeight: 800,
                color: "var(--text-primary)",
                lineHeight: 1,
              }}>
                {scorePct.toFixed(1)}%
              </span>
            </motion.div>
          )}

          {categories.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.5 }}
              style={{
                marginTop: "24px",
                padding: "24px",
                borderRadius: "14px",
                background: "rgba(255,255,255,0.04)",
                border: "1px solid var(--border-subtle)",
                width: "100%",
                maxWidth: "520px",
                textAlign: "left",
              }}
            >
              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "18px",
                fontWeight: 700,
                color: "var(--text-primary)",
                fontSize: "15px",
              }}>
                <FiBarChart2 /> Score by Category
              </div>
              {categories.map((c) => (
                <div key={c.category} style={{ marginBottom: "14px" }}>
                  <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                    fontSize: "13px",
                    marginBottom: "6px",
                    color: "var(--text-primary)",
                    gap: "16px",
                  }}>
                    <span style={{ fontWeight: 600 }}>{c.category}</span>
                    <span style={{
                      color: "var(--text-primary)",
                      fontVariantNumeric: "tabular-nums",
                      whiteSpace: "nowrap",
                    }}>
                      {c.score_percentage.toFixed(1)}%
                    </span>
                  </div>
                  <div style={{
                    height: "6px",
                    borderRadius: "3px",
                    background: "rgba(255,255,255,0.08)",
                    overflow: "hidden",
                  }}>
                    <div style={{
                      height: "100%",
                      width: `${c.score_percentage}%`,
                      background: "var(--text-primary)",
                      transition: "width 0.4s ease",
                    }} />
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          <motion.button
            className="btn-primary btn-large"
            onClick={() => navigate("/welcome")}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            style={{ marginTop: "24px" }}
          >
            <FiHome /> Back to Home
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
}
